import { Subscription, User } from '@/store/user/types';

import type { PauseResponse } from './hooks';

/**
 * Reads the server-derived `pause_eligible` flag — single source of
 * truth, computed by the same controller code that enforces eligibility
 * on POST in calm/api (app/services/subscriptions/sources/stripe/controller.ts).
 * Returns false when the field is absent so ineligible CTAs stay hidden
 * during the backend rollout window.
 */
export const isPauseEligible = (subscription?: Subscription): boolean => {
	if (subscription?.type !== 'stripe') return false;
	return subscription.stripe_details?.pause_eligible === true;
};

export type PauseState = 'none' | 'scheduled' | 'active';

/**
 * `scheduled` = pause was set but `paused_starts_at` is still in the future,
 * so the user keeps access until then. `active` = boundary has elapsed and
 * server-side `valid` has flipped to false.
 *
 * Prefer the server-derived `pause_state` field: calm/api computes it with an
 * effective clock, so it follows a Stripe test clock in dev QA and
 * is immune to device-clock skew in prod. We only fall back to deriving the
 * state from the raw dates against a browser `Date.now()` during the backend
 * cache-rollover window, when older payloads don't carry `pause_state` yet.
 *
 * Fallback semantics: we accept `stripe_collection_paused` flipping with
 * `paused_starts_at` still unset (webhook arrived before the pause endpoint
 * persisted its write, or vice-versa) as a transient `scheduled` state — same
 * fallback the backend isValid() uses. Conversely, if `paused_resumes_at` is
 * already in the past we treat the pause as over even when the resume webhook
 * hasn't landed yet, to avoid pinning the UI on `active`.
 */
export const getPauseState = (subscription?: Subscription): PauseState => {
	const details = subscription?.stripe_details;
	if (!details) return 'none';

	if (details.pause_state) return details.pause_state;

	const paused = details.stripe_collection_paused;
	const startsAt = details.paused_starts_at;
	const resumesAt = details.paused_resumes_at;
	if (!paused && !startsAt) return 'none';

	const now = Date.now();
	if (resumesAt && new Date(resumesAt).getTime() <= now) return 'none';

	if (!startsAt) return 'scheduled';
	const lapsed = new Date(startsAt).getTime() <= now;
	return lapsed ? 'active' : 'scheduled';
};

/**
 * Optimistically reflect a successful `POST /pause` in the user store.
 *
 * The pause endpoint is a thin wrapper: it sets Stripe state + embeds Calm
 * intent as metadata, but the local row's pause fields
 * (`stripe_collection_paused`, `paused_starts_at`, `paused_resumes_at`) are
 * written by the `customer.subscription.updated` webhook, which lands a few
 * seconds later (calm/api 469e428e71, "webhook-driven single-writer"). So a
 * refetch immediately after pausing races the webhook and can come back
 * still-unpaused.
 *
 * We close that window by merging the authoritative dates from the API
 * response straight into the store, exactly as the backend handoff doc
 * recommends. `pause_state: 'scheduled'` drives the scheduled banner
 * immediately (a fresh pause always starts at a future boundary), and
 * `pause_eligible: false` keeps the pause offer from re-appearing until the
 * webhook reconciles on the next load.
 */
export const applyOptimisticPause = (user: User | null, pause: PauseResponse): User | null => {
	const subscription = user?.subscription;
	if (!user || subscription?.type !== 'stripe' || !subscription.stripe_details) return user;
	return {
		...user,
		subscription: {
			...subscription,
			stripe_details: {
				...subscription.stripe_details,
				stripe_collection_paused: true,
				paused_starts_at: pause.paused_starts_at,
				paused_resumes_at: pause.paused_resumes_at,
				pause_eligible: false,
				pause_state: 'scheduled',
			},
		},
	};
};

/**
 * Optimistically reflect a successful `POST /resume` in the user store — the
 * mirror of {@link applyOptimisticPause}. Dispatched immediately on resume
 * success (not deferred to the success screen's Done button) so dismissing the
 * modal via X / backdrop / ESC can't leave the manage page showing a stale
 * paused state until the webhook reconciles.
 *
 * Clears the pause fields, restores `pause_eligible`/`pause_state`, and flips
 * `valid` back to true: a post-lapse resume charges a fresh term, and an active
 * pause had `valid: false`, so without this the page would fall through to the
 * "expired" copy after the modal closes.
 */
export const applyOptimisticResume = (user: User | null): User | null => {
	const subscription = user?.subscription;
	if (!user || subscription?.type !== 'stripe' || !subscription.stripe_details) return user;
	return {
		...user,
		subscription: {
			...subscription,
			valid: true,
			stripe_details: {
				...subscription.stripe_details,
				stripe_collection_paused: false,
				paused_starts_at: null,
				paused_resumes_at: null,
				pause_eligible: true,
				pause_state: 'none',
			},
		},
	};
};
